import React from "react"
import {
  PollData,
  ErrorPoll,
  UnknownPoll,
  PendingPoll,
  SuccessPoll,
  downloadStatisticsLink,
  getPollResult,
} from "../data/api"
import {
  addErrorAction,
  setReportAction,
  setAppStatus,
  DispatchProp,
  ModelProp,
} from "./Form/update"
import { Just } from "purify-ts"
import SearchIndicator from "@nvdb-rapport/shared/src/components/SearchIndicator"
import "twin.macro"
import Head from "@nvdb-rapport/shared/src/components/Head"
import { LoadingReportSpinner } from "@nvdb-rapport/shared/src/components/LoadingReportSpinner"

// Poll Side-effects

export function pollData({ dispatch, model }: DispatchProp & ModelProp): void {
  return model.reportData.caseOf({
    Nothing: () => {
      "Kunne ikke starte polling. model.reportData har ikke blitt tillagt en verdi, men timeren startet likevel."
    },
    Just: async pollData => {
      const pollResult = await getPollResult(pollData.id).run()
      pollResult
        .ifRight(poll => {
          switch (poll.status) {
            case "SUCCESS":
              const successPoll = poll as SuccessPoll
              dispatch(setReportAction(Just(successPoll)))
              dispatch(setAppStatus({ type: "FetchedReport" }))
              break
            case "PENDING":
              const pendingPoll = poll as PendingPoll

              dispatch(setReportAction(Just(pendingPoll)))
              break
            case "UNKNOWN":
              const unknowPoll = poll as UnknownPoll
              dispatch(setReportAction(Just(unknowPoll)))
              dispatch(
                addErrorAction({
                  title: "Polling feilet: Unkown",
                  message: poll.status,
                })
              )

              break
            case "ERROR":
              const errorPoll = poll as ErrorPoll
              dispatch(setReportAction(Just(errorPoll)))
              dispatch(
                addErrorAction({
                  title: `POLL ERROR: (${errorPoll.durationMsString})`,
                  message: errorPoll.error,
                })
              )
              break
          }
        })
        .ifLeft(e => {
          dispatch(
            addErrorAction({
              title: "Feil ved innhenting av data",
              message: e,
            })
          )
          console.error("Noe gikk galt med polling", e)
        })
    },
  })
}

// VIEW

function foldPollData<R>(
  pollData: PollData,
  error: (data: ErrorPoll) => R,
  unknown: (data: UnknownPoll) => R,
  pending: (data: PendingPoll) => R,
  success: (data: SuccessPoll) => R
): R {
  switch (pollData.status) {
    case "ERROR":
      return error(pollData)
    case "UNKNOWN":
      return unknown(pollData)
    case "PENDING":
      return pending(pollData)
    case "SUCCESS":
      return success(pollData)
  }
}

export default function PollDataView({ pollData }: { pollData: PollData }) {
  return (
    <div style={{ marginTop: "2rem" }}>
      {foldPollData(
        pollData,
        () => (
          <></>
        ),
        () => (
          <>
            <Head siteTitle="Feil" status="red" />
          </>
        ),
        pendingData => (
          <>
            <Pending pendingData={pendingData} />
          </>
        ),
        successData => (
          <>
            <Success successData={successData} />
          </>
        )
      )}
    </div>
  )
}

function Pending({ pendingData }: { pendingData: PendingPoll }) {
  return (
    <LoadingReportSpinner durationMsString={pendingData.durationMsString} />
  )
}

function Success({ successData }: { successData: SuccessPoll }) {
  return (
    <>
      <Head siteTitle="Laster" status="green" />
      <div>Rapporten er klar</div>
      <DownloadButton
        link={downloadStatisticsLink(successData.rapport.nedlastLink, "XLSX")}
      ></DownloadButton>
    </>
  )
}

function DownloadButton({ link }: { link: string }) {
  return (
    <a
      href={link}
      tw="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded inline-flex items-center w-full flex justify-center"
    >
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="fas"
        data-icon="file-excel"
        tw="fill-current w-4 h-4 mr-2"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 384 512"
      >
        <path
          fill="currentColor"
          d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm60.1 106.5L224 336l60.1 93.5c5.1 8-.6 18.5-10.1 18.5h-34.9c-4.4 0-8.5-2.4-10.6-6.3C208.9 405.5 192 373 192 373c-6.4 14.8-10 20-36.6 68.8-2.1 3.9-6.1 6.3-10.5 6.3H110c-9.5 0-15.2-10.5-10.1-18.5l60.3-93.5-60.3-93.5c-5.2-8 .6-18.5 10.1-18.5h34.8c4.4 0 8.5 2.4 10.6 6.3 26.1 48.8 20 33.6 36.6 68.5 0 0 6.1-11.7 36.6-68.5 2.1-3.9 6.2-6.3 10.6-6.3H274c9.5-.1 15.2 10.4 10.1 18.4zM384 121.9v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"
        ></path>
      </svg>
      <span>Last ned (.xlsx)</span>
    </a>
  )
}
